import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-all duration-150 active:translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 focus-visible:ring-offset-bg-primary disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'bg-emerald-600 bg-gradient-to-b from-white/25 to-white/0 text-white hover:bg-emerald-700 shadow-[0_2px_4px_rgba(0,0,0,0.5),0_0_14px_rgba(16,185,129,0.25),inset_0_1px_0_rgba(255,255,255,0.3)] hover:shadow-[0_3px_14px_rgba(16,185,129,0.45),inset_0_1px_0_rgba(255,255,255,0.3)]',
        destructive:
          'bg-red-600 bg-gradient-to-b from-white/25 to-white/0 text-white hover:bg-red-700 shadow-[0_2px_4px_rgba(0,0,0,0.5),inset_0_1px_0_rgba(255,255,255,0.3)] hover:shadow-[0_3px_14px_rgba(239,68,68,0.4),inset_0_1px_0_rgba(255,255,255,0.3)]',
        outline:
          'border border-white/25 bg-gradient-to-b from-white/10 to-white/0 text-text-primary hover:bg-white/10 shadow-[0_1px_3px_rgba(0,0,0,0.4),inset_0_1px_0_rgba(255,255,255,0.1)]',
        secondary:
          'bg-white/10 bg-gradient-to-b from-white/10 to-white/0 text-text-primary hover:bg-white/20 shadow-[0_1px_3px_rgba(0,0,0,0.4),inset_0_1px_0_rgba(255,255,255,0.15)]',
        ghost: 'hover:bg-white/10 text-text-secondary',
        link: 'text-emerald-500 underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
